import React, { useState, useEffect } from 'react';
import { db } from '../config/firebaseConfig';
import { getDocs, collection, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { Button, Card, Modal, Form, Alert, Badge, Row, Col } from 'react-bootstrap';

const YemekListesiDuzenle = () => {
  const [yemekler, setYemekler] = useState([]);
  const [mesaj, setMesaj] = useState(null);
  const [hata, setHata] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedYemek, setSelectedYemek] = useState(null);
  const [yemekAdi, setYemekAdi] = useState('');
  const [tarih, setTarih] = useState('');
  const [kategori, setKategori] = useState('');
  const [malzemeler, setMalzemeler] = useState([]);
  const [yeniMalzeme, setYeniMalzeme] = useState('');
  const [tarif, setTarif] = useState('');

  // Fetch all meals from Firebase
  const fetchYemekler = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'yemekListesi'));
      const yemekListesi = [];
      querySnapshot.forEach((doc) => {
        yemekListesi.push({ id: doc.id, ...doc.data() });
      });
      setYemekler(yemekListesi);
    } catch (error) {
      console.error('Yemekler yüklenirken hata:', error);
      setHata('Yemekler yüklenirken bir hata oluştu.');
    }
  };

  // Fetch meals on component mount
  useEffect(() => {
    fetchYemekler();
  }, []);

  // Open modal with selected meal's data
  const handleCardClick = (yemek) => {
    setSelectedYemek(yemek);
    setYemekAdi(yemek.yemekAdi);
    setTarih(yemek.tarih);
    setKategori(yemek.kategori);
    setMalzemeler(yemek.malzemeler);
    setTarif(yemek.tarif);
    setShowModal(true);
  };

  // Handle adding ingredients (supports comma-separated input)
  const handleMalzemeEkle = (e) => {
    e.preventDefault();
    if (yeniMalzeme.trim()) {
      const yeniMalzemeler = yeniMalzeme
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item)
        .map((item) => ({ ad: item, satinAlindi: false }));
      setMalzemeler([...malzemeler, ...yeniMalzemeler]);
      setYeniMalzeme('');
    }
  };

  // Delete an ingredient from the form
  const handleMalzemeSil = (index) => {
    setMalzemeler(malzemeler.filter((_, i) => i !== index));
  };

  // Handle updating a meal
  const handleUpdate = async (e) => {
    e.preventDefault();
    setMesaj(null);
    setHata(null);

    if (!yemekAdi || !tarih || !kategori || malzemeler.length === 0 || !tarif) {
      setHata('Lütfen tüm alanları doldurun.');
      return;
    }

    try {
      const yemekRef = doc(db, 'yemekListesi', selectedYemek.id);
      await updateDoc(yemekRef, {
        yemekAdi,
        tarih,
        kategori,
        malzemeler,
        tarif,
        updatedAt: new Date(),
      });

      setMesaj('Yemek başarıyla güncellendi!');
      setShowModal(false);
      fetchYemekler(); // Refresh the list
    } catch (error) {
      console.error('Yemek güncellenirken hata:', error);
      setHata('Yemek güncellenirken bir hata oluştu.');
    }
  };

  // Handle deleting a meal
  const handleDelete = async () => {
    try {
      await deleteDoc(doc(db, 'yemekListesi', selectedYemek.id));
      setMesaj('Yemek başarıyla silindi!');
      setShowModal(false);
      fetchYemekler(); // Refresh the list
    } catch (error) {
      console.error('Yemek silinirken hata:', error);
      setHata('Yemek silinirken bir hata oluştu.');
    }
  };

  // Close modal and reset form
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedYemek(null);
    setYemekAdi('');
    setTarih('');
    setKategori('');
    setMalzemeler([]);
    setYeniMalzeme('');
    setTarif('');
    setMesaj(null);
    setHata(null);
  };

  return (
    <div className="yemek-listesi-duzenle-container">
      <div className="yemek-listesi-duzenle-section-title text-center mb-5">
        <h2>Yemek Listesini Düzenle</h2>
        <div className="yemek-listesi-duzenle-title-shape">
          <svg viewBox="0 0 200 20" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M 0,10 C 40,0 60,20 100,10 C 140,0 160,20 200,10"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
        </div>
        <p className="yemek-listesi-duzenle-lead">
          Yemek kartlarına tıklayarak düzenleyin veya silin.
        </p>
      </div>

      {mesaj && <Alert variant="success">{mesaj}</Alert>}
      {hata && <Alert variant="danger">{hata}</Alert>}

      {yemekler.length === 0 ? (
        <Alert variant="info">Yemek listesi boş.</Alert>
      ) : (
        <Row xs={1} md={2} lg={3} className="g-4">
          {yemekler.map((yemek) => (
            <Col key={yemek.id}>
              <Card
                className="yemek-card shadow-sm"
                onClick={() => handleCardClick(yemek)}
                role="button"
                aria-label={`Düzenle veya sil ${yemek.yemekAdi}`}
              >
                <Card.Body>
                  <Card.Title className="yemek-card-title">{yemek.yemekAdi}</Card.Title>
                  <Card.Subtitle className="mb-2 text-muted">
                    {yemek.tarih} | {yemek.kategori}
                  </Card.Subtitle>
                  <div className="yemek-card-malzemeler mb-2">
                    <strong>Malzemeler:</strong>
                    <div>
                      {yemek.malzemeler.map((malzeme, index) => (
                        <Badge
                          key={index}
                          bg={malzeme.satinAlindi ? 'success' : 'warning'}
                          className="me-1 mb-1"
                        >
                          {malzeme.ad}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <Card.Text>
                    <strong>Tarif:</strong> {yemek.tarif.slice(0, 100)}...
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Modal for Editing/Deleting */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedYemek?.yemekAdi} - Düzenle veya Sil</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {mesaj && <Alert variant="success">{mesaj}</Alert>}
          {hata && <Alert variant="danger">{hata}</Alert>}

          <Form onSubmit={handleUpdate}>
            <Form.Group className="mb-3" controlId="editYemekAdi">
              <Form.Label>Yemek Adı</Form.Label>
              <Form.Control
                type="text"
                value={yemekAdi}
                onChange={(e) => setYemekAdi(e.target.value)}
                placeholder="Ör: Mercimek Çorbası"
                aria-label="Yemek adı"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="editTarih">
              <Form.Label>Tarih</Form.Label>
              <Form.Control
                type="date"
                value={tarih}
                onChange={(e) => setTarih(e.target.value)}
                aria-label="Yemek tarihi"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="editKategori">
              <Form.Label>Kategori</Form.Label>
              <Form.Select
                value={kategori}
                onChange={(e) => setKategori(e.target.value)}
                aria-label="Yemek kategorisi"
                required
              >
                <option value="">Kategori Seçin</option>
                <option value="Çorba">Çorba</option>
                <option value="Ana Yemek">Ana Yemek</option>
                <option value="Tatlı">Tatlı</option>
                <option value="Salata">Salata</option>
                <option value="Diğer">Diğer</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3" controlId="editMalzemeler">
              <Form.Label>Alışveriş Listesi</Form.Label>
              <div className="d-flex mb-2">
                <Form.Control
                  type="text"
                  value={yeniMalzeme}
                  onChange={(e) => setYeniMalzeme(e.target.value)}
                  placeholder="Ör: domates,soğan,pırasa"
                  aria-label="Yeni malzeme"
                />
                <Button
                  variant="outline-success"
                  className="ms-2"
                  onClick={handleMalzemeEkle}
                  aria-label="Malzeme ekle"
                >
                  Ekle
                </Button>
              </div>
              {malzemeler.length > 0 && (
                <ul className="yemek-listesi-duzenle-malzeme-list list-group mb-2">
                  {malzemeler.map((malzeme, index) => (
                    <li
                      key={index}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      <span className={malzeme.satinAlindi ? 'text-decoration-line-through text-muted' : ''}>
                        {malzeme.ad}
                      </span>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleMalzemeSil(index)}
                        aria-label={`Sil ${malzeme.ad}`}
                      >
                        Sil
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </Form.Group>

            <Form.Group className="mb-3" controlId="editTarif">
              <Form.Label>Tarif</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={tarif}
                onChange={(e) => setTarif(e.target.value)}
                placeholder="Yemeğin hazırlanış talimatlarını buraya yazın..."
                aria-label="Yemek tarifi"
                required
              />
            </Form.Group>

            <div className="d-flex justify-content-between">
              <Button variant="success" type="submit" aria-label="Yemeği güncelle">
                Güncelle
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                aria-label={`Sil ${selectedYemek?.yemekAdi}`}
              >
                Sil
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default YemekListesiDuzenle;